import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { profileService } from "../lib/profileService";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

export function ProfileSetup() {
  const { user, setProfileCompleted } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    username: "",
    companyName: "",
    contactNumber: "",
  });
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};

    if (!formData.username.trim()) {
      newErrors.username = "Nome de usuário é obrigatório";
    }

    if (!formData.companyName.trim()) {
      newErrors.companyName = "Nome da empresa é obrigatório";
    }

    if (!formData.contactNumber.trim()) {
      newErrors.contactNumber = "Número de contato é obrigatório";
    } else if (!/^\d{10,11}$/.test(formData.contactNumber.replace(/\D/g, ""))) {
      newErrors.contactNumber =
        "Número de contato inválido (use apenas números)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("[PROFILE-SETUP] Submitting form with data:", formData);

    if (!validateForm()) {
      console.log("[PROFILE-SETUP] Form validation failed");
      return;
    }

    setLoading(true);
    try {
      console.log("[PROFILE-SETUP] Creating profile...");
      const response = await profileService.createProfile(formData);
      console.log("[PROFILE-SETUP] Profile created successfully:", response);

      // Update auth context with the completed profile
      console.log("[PROFILE-SETUP] Updating auth context with profile...");
      setProfileCompleted(response);
      console.log("[PROFILE-SETUP] Auth context updated");

      // Navigate to dashboard
      console.log("[PROFILE-SETUP] Navigating to dashboard...");
      navigate("/dashboard");
    } catch (error) {
      console.error("Error saving profile:", error);
      alert("Erro ao salvar perfil: " + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-6">
      {/* Background GIF */}
      <div
        className="absolute inset-0 z-0"
        style={{
          backgroundImage: "url(/fundo.gif)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      />
      {/* Overlay for better readability */}
      <div className="absolute inset-0 bg-black/30 z-0" />

      {/* Content */}
      <Card className="w-full max-w-md relative z-10 shadow-2xl">
        <CardHeader>
          <CardTitle className="text-2xl text-center">
            Complete seu Perfil
          </CardTitle>
          <CardDescription className="text-center">
            Precisamos de algumas informações para personalizar sua experiência
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="username">Nome de Usuário</Label>
              <Input
                id="username"
                name="username"
                type="text"
                placeholder="João Silva"
                value={formData.username}
                onChange={handleChange}
                className={errors.username ? "border-red-500" : ""}
              />
              {errors.username && (
                <p className="text-sm text-red-500">{errors.username}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="companyName">Nome da Empresa</Label>
              <Input
                id="companyName"
                name="companyName"
                type="text"
                placeholder="Minha Empresa Ltda"
                value={formData.companyName}
                onChange={handleChange}
                className={errors.companyName ? "border-red-500" : ""}
              />
              {errors.companyName && (
                <p className="text-sm text-red-500">{errors.companyName}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="contactNumber">Número de Contato</Label>
              <Input
                id="contactNumber"
                name="contactNumber"
                type="tel"
                placeholder="67991234567"
                value={formData.contactNumber}
                onChange={handleChange}
                className={errors.contactNumber ? "border-red-500" : ""}
              />
              {errors.contactNumber && (
                <p className="text-sm text-red-500">{errors.contactNumber}</p>
              )}
              <p className="text-xs text-gray-500">
                Digite apenas números (DDD + número)
              </p>
            </div>

            <Button
              type="submit"
              className="w-full bg-vitapink hover:bg-vitapink-700"
              disabled={loading}
            >
              {loading ? "Salvando..." : "Continuar"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
